/**
 * Back In Stock Notification
 *
 * Shows a "Notify Me" trigger button when a sold-out variant is selected.
 * Button opens the shared <notification-modal>. Submit posts directly to
 * Klaviyo's client-side Back In Stock subscription API with name + phone
 * attached to the profile (phone stored on profile only — no SMS channel).
 *
 * Listens on document for variantChange events so it survives combined
 * listing innerHTML replacements.
 */
(function () {
  'use strict';

  var KLAVIYO_BIS_URL = 'https://a.klaviyo.com/client/back-in-stock-subscriptions';
  var KLAVIYO_PROFILES_URL = 'https://a.klaviyo.com/client/profiles';
  var KLAVIYO_API_REVISION = '2026-01-15';

  var currentVariantId = null;
  var formSubmitted = false;

  // Re-queried after combined-listing switches
  var purchaseControls = null;
  var bisControls = null;
  var trigger = null;
  var modal = null;
  var form = null;
  var submitBtn = null;
  var messageEl = null;
  var originalButtonText = '';
  var successText = '';

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    if (typeof klaviyoPublicKey === 'undefined' || !klaviyoPublicKey) {
      console.warn('Back In Stock: klaviyoPublicKey is not configured in head.liquid');
      return;
    }

    if (!queryElements()) return;

    // Delegated listeners — survive innerHTML replacements by product-info.js
    document.addEventListener('variantChange', handleVariantChange);
    document.addEventListener('optionValueChange', handleOptionValueChange);
    document.addEventListener('submit', handleDelegatedSubmit, true);

    // Show BIS controls on load if: (1) sold-out variant preselected via URL,
    // or (2) whole product is sold out.
    var initialVariant = getSelectedVariant();
    if (initialVariant && !initialVariant.available) {
      currentVariantId = String(initialVariant.id);
      if (modal) modal.setAttribute('data-variant-id', currentVariantId);
      showBackInStockControls();
    } else if (bisControls && bisControls.getAttribute('data-product-available') === 'false') {
      showBackInStockControls();
    }
  }

  // ---------------------------------------------------------------------------
  // Element references
  // ---------------------------------------------------------------------------

  function queryElements() {
    var productInfo = document.querySelector('product-info');
    if (!productInfo) return false;

    purchaseControls = productInfo.querySelector('[data-purchase-controls]');
    bisControls = productInfo.querySelector('[data-back-in-stock-controls]');
    if (!purchaseControls || !bisControls) return false;

    trigger = bisControls.querySelector('[data-notification-trigger="back-in-stock"]');
    if (!trigger) return false;

    var modalId = trigger.getAttribute('data-open-notification-modal');
    modal = modalId ? document.getElementById(modalId) : null;
    if (!modal) return false;

    form = modal.querySelector('[data-notification-form]');
    submitBtn = form ? form.querySelector('[data-notification-submit]') : null;
    messageEl = form ? form.querySelector('.notification-modal__message') : null;
    originalButtonText = submitBtn ? submitBtn.textContent.trim() : 'Notify Me';
    successText = trigger.getAttribute('data-success-text') || 'Notification Set';

    return Boolean(form && submitBtn);
  }

  // ---------------------------------------------------------------------------
  // Event handlers
  // ---------------------------------------------------------------------------

  function handleDelegatedSubmit(event) {
    var submittedForm = event.target;
    if (!submittedForm.matches || !submittedForm.matches('[data-notification-form]')) return;
    // Route by the parent <notification-modal data-notification-type> —
    // the modal lives outside [data-back-in-stock-controls] in the DOM
    // (hoisted so display:none toggles on the wrapper don't hide it), so we
    // can't rely on ancestor traversal to decide ownership.
    var modal = submittedForm.closest('notification-modal');
    if (!modal || modal.getAttribute('data-notification-type') !== 'back-in-stock') return;
    event.preventDefault();
    // Re-query in case combined-listing replaced DOM
    queryElements();
    handleSubmit();
  }

  function handleVariantChange(event) {
    var detail = event.detail;
    if (!detail || !detail.variantId) return;

    if (!bisControls || !bisControls.isConnected) {
      if (!queryElements()) return;
    }

    var isAvailable = detail.variantAvailable === 'true' || detail.variantAvailable === true;
    var variantId = String(detail.variantId);

    if (isAvailable) {
      showPurchaseControls();
      currentVariantId = null;
      formSubmitted = false;
    } else {
      var isNewVariant = variantId !== currentVariantId;
      currentVariantId = variantId;
      if (modal) modal.setAttribute('data-variant-id', variantId);

      // Reset form state on variant switch so success state from a previous
      // submission doesn't persist across sold-out sizes.
      if (isNewVariant) resetForm();

      showBackInStockControls();
    }
  }

  function handleOptionValueChange(event) {
    var detail = event.detail;
    if (!detail || !detail.isProductSwitch) return;

    // Combined listing switch — elements will be replaced. Drop refs.
    currentVariantId = null;
    formSubmitted = false;
    purchaseControls = null;
    bisControls = null;
    trigger = null;
    modal = null;
    form = null;
  }

  // ---------------------------------------------------------------------------
  // Visibility toggles
  // ---------------------------------------------------------------------------

  function showPurchaseControls() {
    if (bisControls) bisControls.style.display = 'none';
    if (purchaseControls) purchaseControls.style.display = '';
  }

  function showBackInStockControls() {
    if (purchaseControls) purchaseControls.style.display = 'none';
    if (bisControls) bisControls.style.display = '';
  }

  // ---------------------------------------------------------------------------
  // Form submission
  // ---------------------------------------------------------------------------

  function handleSubmit() {
    var fields = readFields(modal);

    if (!fields.email || !isValidEmail(fields.email)) {
      showMessage('Please enter a valid email address.', 'error');
      return;
    }
    if (!fields.first_name) {
      showMessage('Please enter your first name.', 'error');
      return;
    }
    if (!currentVariantId) {
      showMessage('Please select a size first.', 'error');
      return;
    }

    submitBtn.disabled = true;
    submitBtn.textContent = 'Sending...';
    hideMessage();

    // Two-step flow:
    //   1. Upsert profile via /client/profiles with full attrs (name + phone).
    //      The BIS-subscription endpoint only accepts identifier fields
    //      (email / phone_number) on its embedded profile, so name fields
    //      must land via this separate call.
    //   2. POST BIS subscription with email (+ phone_number) identifiers.
    //
    // Both are client-side public-key calls; we fire step 1 first and
    // treat its failure as non-fatal for the BIS subscription itself —
    // the important thing is the subscription registers. Name is a nice-
    // to-have for flow personalisation.

    upsertProfile(fields)
      .catch(function (err) {
        // Non-fatal — BIS subscription is the primary win; profile attrs
        // are a nice-to-have for flow personalisation.
        console.warn('Back In Stock: profile upsert failed, continuing:', err.body || err.message);
      })
      .then(function () {
        return subscribeBackInStock(fields);
      })
      .then(function () {
        showSuccess();
        formSubmitted = true;
      })
      .catch(function (error) {
        console.error('Back In Stock error:', error);
        var friendly = parseKlaviyoError(error && error.body) ||
          'Something went wrong. Please try again.';
        showMessage(friendly, 'error');
        submitBtn.disabled = false;
        submitBtn.textContent = originalButtonText;
      });
  }

  // Klaviyo returns { errors: [{ detail, title, source, code, ... }] } on
  // failure. Surface the first error's detail so the user can self-correct
  // (e.g. "Invalid phone number format"). A few codes get friendlier copy
  // because the raw detail would confuse a customer — `variant_not_found`
  // in particular happens on combined-listing siblings whose sibling
  // product isn't synced to the Klaviyo catalog (see the known-limitations
  // doc).
  function parseKlaviyoError(body) {
    if (!body) return null;
    try {
      var parsed = typeof body === 'string' ? JSON.parse(body) : body;
      if (parsed && parsed.errors && parsed.errors.length) {
        var first = parsed.errors[0];
        if (first.code === 'variant_not_found') {
          return "Sorry, notifications aren't available for this product right now. Please try a different size or check back later.";
        }
        return first.detail || first.title || null;
      }
    } catch (e) {}
    return null;
  }

  function upsertProfile(fields) {
    var attributes = { email: fields.email };
    if (fields.first_name) attributes.first_name = fields.first_name;
    if (fields.last_name) attributes.last_name = fields.last_name;
    if (fields.phone) attributes.phone_number = fields.phone;

    return fetch(KLAVIYO_PROFILES_URL + '?company_id=' + encodeURIComponent(klaviyoPublicKey), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: KLAVIYO_API_REVISION
      },
      body: JSON.stringify({
        data: {
          type: 'profile',
          attributes: attributes
        }
      })
    }).then(function (response) {
      // 202 = accepted, 409 = profile exists (safe to proceed), 2xx generally ok
      if (!response.ok && response.status !== 409) {
        return response.text().then(function (text) {
          var err = new Error('Profile upsert failed: ' + response.status);
          err.status = response.status;
          err.body = text;
          throw err;
        });
      }
      return response;
    });
  }

  function subscribeBackInStock(fields) {
    var klaviyoVariantId = '$shopify:::$default:::' + currentVariantId;

    // Only identifier fields allowed on the embedded profile here.
    var profileAttrs = { email: fields.email };
    if (fields.phone) profileAttrs.phone_number = fields.phone;

    return fetch(KLAVIYO_BIS_URL + '?company_id=' + encodeURIComponent(klaviyoPublicKey), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: KLAVIYO_API_REVISION
      },
      body: JSON.stringify({
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: { data: { type: 'profile', attributes: profileAttrs } },
            channels: ['EMAIL']
          },
          relationships: {
            variant: { data: { type: 'catalog-variant', id: klaviyoVariantId } }
          }
        }
      })
    }).then(function (response) {
      if (!response.ok) {
        return response.text().then(function (text) {
          var err = new Error('Klaviyo BIS request failed: ' + response.status);
          err.status = response.status;
          err.body = text;
          throw err;
        });
      }
      return response;
    });
  }

  // ---------------------------------------------------------------------------
  // UI helpers
  // ---------------------------------------------------------------------------

  function showSuccess() {
    submitBtn.disabled = true;
    submitBtn.classList.remove('btn--black');
    submitBtn.classList.add('notification-modal__submit--success');
    submitBtn.textContent = successText;
  }

  function resetForm() {
    formSubmitted = false;
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.classList.remove('notification-modal__submit--success');
      submitBtn.classList.add('btn--black');
      submitBtn.textContent = originalButtonText;
    }
    hideMessage();
  }

  function getSelectedVariant() {
    var el = document.querySelector('[data-selected-variant]');
    if (!el) return null;
    try {
      var data = JSON.parse(el.textContent.trim());
      return data && data.id ? data : null;
    } catch (e) {
      return null;
    }
  }

  function readFields(modal) {
    function val(sel) {
      var el = modal.querySelector(sel);
      return el && el.value ? el.value.trim() : '';
    }
    return {
      email: val('[data-field-email]'),
      first_name: val('[data-field-first-name]'),
      last_name: val('[data-field-last-name]'),
      phone: normalisePhone(val('[data-field-phone]'))
    };
  }

  // Klaviyo requires E.164 (+countrycode + digits). The modal shows a visible
  // "+" prefix so customers know to lead with a country code; we normalise
  // whatever they typed to match:
  //   - Strip all punctuation / whitespace
  //   - Leave a leading "+" alone
  //   - AU local (10 digits starting with 0) → rewrite as +61
  //   - Otherwise prepend "+" so the server side has a valid E.164 shape to
  //     hand to Klaviyo (Klaviyo will still reject if country code is bogus;
  //     we surface their error detail to the user).
  function normalisePhone(raw) {
    if (!raw) return '';
    var cleaned = raw.replace(/[\s\-().]/g, '');
    if (!cleaned) return '';
    if (cleaned.charAt(0) === '+') return cleaned;
    if (/^0\d{9}$/.test(cleaned)) return '+61' + cleaned.slice(1);
    return '+' + cleaned;
  }

  function showMessage(text, type) {
    if (!messageEl) return;
    messageEl.textContent = text;
    messageEl.hidden = false;
    messageEl.className = 'notification-modal__message notification-modal__message--' + type;
  }

  function hideMessage() {
    if (!messageEl) return;
    messageEl.hidden = true;
  }

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }
})();
